<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ProductsAPI from 'dashboard/api/products';
import ProductCategoriesAPI from 'dashboard/api/productCategories';
import Drawer from './Drawer.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Textarea from 'dashboard/components-next/textarea/TextArea.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';
import ProductImageCarousel from './ProductImageCarousel.vue';
import ProductCategoryForm from './ProductCategoryForm.vue';

const props = defineProps({
  product: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close', 'save']);
const { t } = useI18n();

const loading = ref(false);
const images = ref([]);
const datasheet = ref(null);
const datasheetFile = ref(null);
const categories = ref([]);
const loadingCategories = ref(false);
const showCategoryForm = ref(false);

// Options prédéfinies pour les couleurs
const colorOptions = ref([
  { value: 'Rouge', label: 'Rouge' },
  { value: 'Bleu', label: 'Bleu' },
  { value: 'Noir', label: 'Noir' },
  { value: 'Blanc', label: 'Blanc' },
  { value: 'Vert', label: 'Vert' },
  { value: 'Jaune', label: 'Jaune' },
  { value: 'Orange', label: 'Orange' },
  { value: 'Rose', label: 'Rose' },
  { value: 'Violet', label: 'Violet' },
  { value: 'Gris', label: 'Gris' },
  { value: 'Marron', label: 'Marron' },
  { value: 'Beige', label: 'Beige' },
  { value: 'Turquoise', label: 'Turquoise' },
  { value: 'Indigo', label: 'Indigo' },
  { value: 'Cyan', label: 'Cyan' },
  { value: 'Magenta', label: 'Magenta' },
]);

// Options de devises
const currencyOptions = ref([
  { value: 'EUR', label: 'EUR (€)', symbol: '€' },
  { value: 'USD', label: 'USD ($)', symbol: '$' },
  { value: 'GBP', label: 'GBP (£)', symbol: '£' },
  { value: 'CHF', label: 'CHF (Fr)', symbol: 'Fr' },
  { value: 'CAD', label: 'CAD ($)', symbol: '$' },
  { value: 'JPY', label: 'JPY (¥)', symbol: '¥' },
  { value: 'CNY', label: 'CNY (¥)', symbol: '¥' },
]);

const formData = ref({
  name: '',
  sku: '',
  description: '',
  price: 0,
  price_unit: '',
  currency: 'EUR',
  stock: 0,
  weight: 0,
  product_category_id: null,
  active: true,
  available_colors: [],
  tags: [],
});

const isEdit = computed(() => !!props.product);
const modalTitle = computed(() =>
  isEdit.value
    ? t('WEBSITE.PRODUCTS.FORM.EDIT')
    : t('WEBSITE.PRODUCTS.FORM.NEW_PRODUCT')
);

const fetchCategories = async () => {
  loadingCategories.value = true;
  try {
    const response = await ProductCategoriesAPI.get();
    // Trier les catégories par nom pour cohérence avec la page ProductCategoriesIndex
    categories.value = response.data
      .map(cat => ({
        value: cat.id,
        label: cat.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  } catch (error) {
    // Erreur silencieuse, les catégories seront vides
  } finally {
    loadingCategories.value = false;
  }
};

const openCategoryForm = () => {
  showCategoryForm.value = true;
};

const closeCategoryForm = () => {
  showCategoryForm.value = false;
};

const handleCategorySave = async newCategory => {
  // Rafraîchir la liste des catégories
  await fetchCategories();

  // Sélectionner automatiquement la nouvelle catégorie
  if (newCategory && newCategory.id) {
    formData.value.product_category_id = newCategory.id;
  }

  closeCategoryForm();
};

onMounted(() => {
  // Rafraîchir les catégories à chaque fois que le formulaire s'ouvre
  fetchCategories();

  if (isEdit.value && props.product) {
    formData.value = {
      name: props.product.name || '',
      sku: props.product.sku || '',
      description: props.product.description || '',
      price: props.product.price || 0,
      price_unit: props.product.price_unit || '',
      currency: props.product.currency || 'EUR',
      stock: props.product.stock || 0,
      weight: props.product.weight || 0,
      product_category_id: props.product.product_category_id || null,
      active: props.product.active !== false,
      available_colors: props.product.available_colors || [],
      tags: props.product.tags || [],
    };

    // Charger les images existantes
    if (props.product.images && props.product.images.length > 0) {
      images.value = props.product.images.map(img => ({
        id: img.id,
        url: img.url,
        filename: img.filename,
        isNew: false,
      }));
    }

    // Charger le datasheet existant
    if (props.product.datasheet) {
      datasheet.value = props.product.datasheet;
    }
  }
});

const handleDatasheetChange = event => {
  const file = event.target.files[0];
  if (file) {
    if (file.type !== 'application/pdf') {
      useAlert('Seuls les fichiers PDF sont acceptés');
      return;
    }
    datasheetFile.value = file;
  }
};

const removeDatasheet = () => {
  datasheet.value = null;
  datasheetFile.value = null;
};

const handleSubmit = async () => {
  loading.value = true;

  try {
    const newImages = images.value.filter(img => img.isNew && img.file);
    const hasNewImages = newImages.length > 0;
    const hasNewDatasheet = datasheetFile.value !== null;

    let payload;

    if (hasNewImages || hasNewDatasheet) {
      // Utiliser FormData si on a des images ou un datasheet
      payload = new FormData();

      Object.keys(formData.value).forEach(key => {
        const value = formData.value[key];

        // Ignorer les valeurs null/undefined
        if (value === null || value === undefined) {
          return;
        }

        if (key === 'available_colors' || key === 'tags') {
          const arrayValue = value || [];
          if (arrayValue.length > 0) {
            arrayValue.forEach(item => {
              payload.append(`product[${key}][]`, item);
            });
          }
        } else if (key === 'active') {
          // Convertir le booléen en string pour FormData
          payload.append(`product[${key}]`, value ? '1' : '0');
        } else if (typeof value === 'number') {
          // S'assurer que les numéros sont bien formatés
          payload.append(`product[${key}]`, value.toString());
        } else if (value !== '') {
          // Ne pas envoyer les chaînes vides
          payload.append(`product[${key}]`, value);
        }
      });

      newImages.forEach(image => {
        payload.append('product[images][]', image.file);
      });

      if (hasNewDatasheet) {
        payload.append('product[datasheet]', datasheetFile.value);
      }
    } else {
      // Utiliser JSON si pas d'images ni de datasheet (meilleure gestion des arrays)
      payload = {
        product: {
          ...formData.value,
          available_colors: formData.value.available_colors || [],
          tags: formData.value.tags || [],
        },
      };
    }

    if (isEdit.value) {
      await ProductsAPI.update(props.product.id, payload);
      useAlert(t('WEBSITE.PRODUCTS.API.SUCCESS.PRODUCT_UPDATED'));
    } else {
      await ProductsAPI.create(payload);
      useAlert(t('WEBSITE.PRODUCTS.API.SUCCESS.PRODUCT_CREATED'));
    }

    emit('save');
  } catch (error) {
    // Erreur déjà gérée par useAlert dans le bloc if/else ci-dessus
    let errorMessage = isEdit.value
      ? t('WEBSITE.PRODUCTS.API.ERROR.UPDATE_FAILED')
      : t('WEBSITE.PRODUCTS.API.ERROR.CREATE_FAILED');

    // Afficher les erreurs détaillées du serveur si disponibles
    if (error.response?.data?.errors) {
      const errors = Array.isArray(error.response.data.errors)
        ? error.response.data.errors.join(', ')
        : Object.values(error.response.data.errors).flat().join(', ');
      errorMessage = `${errorMessage}: ${errors}`;
    } else if (error.response?.data?.error) {
      errorMessage = `${errorMessage}: ${error.response.data.error}`;
    }

    useAlert(errorMessage);
  } finally {
    loading.value = false;
  }
};

const handleClose = () => {
  emit('close');
};
</script>

<template>
  <Drawer show size="md" @close="handleClose">
    <!-- Header -->
    <div
      class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
    >
      <h2 class="text-lg font-semibold text-n-slate-12">
        {{ modalTitle }}
      </h2>
      <button
        class="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-n-alpha-2 text-n-slate-11 transition-colors"
        @click="handleClose"
      >
        <span class="i-lucide-x size-5" />
      </button>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto px-6 py-6">
      <div class="space-y-6">
        <!-- Carousel d'images -->
        <ProductImageCarousel v-model="images" :max-images="5" />

        <!-- Fiche produit PDF -->
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            {{ t('WEBSITE.PRODUCTS.FORM.DATASHEET') }}
          </label>
          <div class="border border-n-weak rounded-lg p-4 bg-n-alpha-1">
            <!-- Affichage du fichier existant ou nouveau -->
            <div
              v-if="datasheet || datasheetFile"
              class="flex items-center justify-between p-3 bg-white border border-n-weak rounded-lg"
            >
              <div class="flex items-center gap-3">
                <span class="i-lucide-file-text size-6 text-n-red-9" />
                <div>
                  <p class="text-sm font-medium text-n-slate-12">
                    {{
                      datasheetFile ? datasheetFile.name : datasheet?.filename
                    }}
                  </p>
                  <p class="text-xs text-n-slate-10">
                    {{
                      datasheetFile
                        ? (datasheetFile.size / 1024).toFixed(2) + ' KB'
                        : (datasheet?.byte_size / 1024).toFixed(2) + ' KB'
                    }}
                  </p>
                </div>
              </div>
              <div class="flex items-center gap-2">
                <a
                  v-if="datasheet && !datasheetFile"
                  :href="datasheet.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="p-2 hover:bg-n-alpha-2 rounded-lg transition-colors"
                  :title="t('WEBSITE.PRODUCTS.FORM.DATASHEET_DOWNLOAD')"
                >
                  <span class="i-lucide-download size-4 text-n-slate-11" />
                </a>
                <button
                  type="button"
                  class="p-2 hover:bg-n-alpha-2 rounded-lg transition-colors"
                  :title="t('WEBSITE.PRODUCTS.FORM.DATASHEET_REMOVE')"
                  @click="removeDatasheet"
                >
                  <span class="i-lucide-trash-2 size-4 text-n-red-9" />
                </button>
              </div>
            </div>

            <!-- Zone d'upload -->
            <div v-else>
              <label
                class="flex flex-col items-center justify-center p-6 border-2 border-dashed border-n-weak rounded-lg cursor-pointer hover:border-n-blue-9 hover:bg-n-alpha-2 transition-colors"
              >
                <span class="i-lucide-upload size-8 text-n-slate-10 mb-2" />
                <span class="text-sm font-medium text-n-slate-12 mb-1">
                  {{ t('WEBSITE.PRODUCTS.FORM.DATASHEET_UPLOAD') }}
                </span>
                <span class="text-xs text-n-slate-10">
                  {{ t('WEBSITE.PRODUCTS.FORM.DATASHEET_FORMAT') }}
                </span>
                <input
                  type="file"
                  accept="application/pdf"
                  class="hidden"
                  @change="handleDatasheetChange"
                />
              </label>
            </div>
          </div>
        </div>

        <!-- Nom du produit -->
        <div>
          <label
            class="block text-sm font-medium text-n-slate-12 mb-1.5"
            for="product-name"
          >
            {{ t('WEBSITE.PRODUCTS.FORM.NAME') }}
            <span class="text-red-500" aria-hidden="true">*</span>
          </label>
          <Input
            id="product-name"
            v-model="formData.name"
            :placeholder="t('WEBSITE.PRODUCTS.FORM.NAME_PLACEHOLDER')"
            required
          />
        </div>

        <!-- SKU -->
        <div>
          <label
            class="block text-sm font-medium text-n-slate-12 mb-1.5"
            for="product-sku"
          >
            {{ t('WEBSITE.PRODUCTS.FORM.SKU') }}
          </label>
          <Input
            id="product-sku"
            v-model="formData.sku"
            :placeholder="t('WEBSITE.PRODUCTS.FORM.SKU_PLACEHOLDER')"
          />
        </div>

        <!-- Catégorie -->
        <div>
          <label
            class="block text-sm font-medium text-n-slate-12 mb-1.5"
            for="product-category"
          >
            {{ t('WEBSITE.PRODUCTS.FORM.CATEGORY') }}
          </label>
          <div class="flex items-center gap-2">
            <div class="flex-1">
              <SelectMenu
                id="product-category"
                v-model="formData.product_category_id"
                :options="categories"
                :placeholder="t('WEBSITE.PRODUCTS.FORM.CATEGORY_PLACEHOLDER')"
                :loading="loadingCategories"
                allow-clear
              />
            </div>
            <button
              type="button"
              class="flex items-center justify-center w-8 h-8 rounded-lg bg-n-blue-3 hover:bg-n-blue-4 text-n-blue-11 hover:text-n-blue-12 border border-n-blue-6 hover:border-n-blue-7 transition-all duration-200 shadow-sm hover:shadow-md flex-shrink-0"
              :title="t('WEBSITE.PRODUCTS.FORM.ADD_CATEGORY')"
              @click="openCategoryForm"
            >
              <span class="i-lucide-plus size-4" />
            </button>
          </div>
        </div>

        <!-- Description -->
        <div>
          <label
            class="block text-sm font-medium text-n-slate-12 mb-1.5"
            for="product-description"
          >
            {{ t('WEBSITE.PRODUCTS.FORM.DESCRIPTION') }}
          </label>
          <Textarea
            id="product-description"
            v-model="formData.description"
            :placeholder="t('WEBSITE.PRODUCTS.FORM.DESCRIPTION_PLACEHOLDER')"
            :rows="4"
          />
        </div>

        <!-- Couleurs disponibles -->
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
            {{ t('WEBSITE.PRODUCTS.FORM.AVAILABLE_COLORS') }}
          </label>
          <TagMultiSelectComboBox
            v-model="formData.available_colors"
            :options="colorOptions"
            :placeholder="
              t('WEBSITE.PRODUCTS.FORM.AVAILABLE_COLORS_PLACEHOLDER')
            "
            :search-placeholder="t('WEBSITE.PRODUCTS.FORM.SEARCH_COLORS')"
            :empty-state="t('WEBSITE.PRODUCTS.FORM.NO_COLORS_FOUND')"
          />
        </div>

        <!-- Tags -->
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
            {{ t('WEBSITE.PRODUCTS.FORM.TAGS') }}
          </label>
          <div
            class="border border-n-weak rounded-lg px-3 py-2 focus-within:border-n-blue-9"
          >
            <TagInput
              v-model="formData.tags"
              :placeholder="t('WEBSITE.PRODUCTS.FORM.TAGS_PLACEHOLDER')"
              allow-create
            />
          </div>
          <p class="mt-1 text-xs text-n-slate-10">
            {{ t('WEBSITE.PRODUCTS.FORM.TAGS_HELP') }}
          </p>
        </div>

        <!-- Prix et Devise -->
        <div class="grid grid-cols-3 gap-4">
          <div>
            <label
              class="block text-sm font-medium text-n-slate-12 mb-1.5"
              for="product-price"
            >
              {{ t('WEBSITE.PRODUCTS.FORM.PRICE_LABEL') }}
            </label>
            <Input
              id="product-price"
              v-model.number="formData.price"
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
            />
          </div>

          <div>
            <label
              class="block text-sm font-medium text-n-slate-12 mb-1.5"
              for="product-currency"
            >
              {{ t('WEBSITE.PRODUCTS.FORM.CURRENCY_LABEL') }}
            </label>
            <SelectMenu
              id="product-currency"
              v-model="formData.currency"
              :options="currencyOptions"
              :placeholder="t('WEBSITE.PRODUCTS.FORM.CURRENCY_PLACEHOLDER')"
            />
          </div>

          <div>
            <label
              class="block text-sm font-medium text-n-slate-12 mb-1.5"
              for="product-price-unit"
            >
              {{ t('WEBSITE.PRODUCTS.FORM.PRICE_UNIT_LABEL') }}
            </label>
            <Input
              id="product-price-unit"
              v-model="formData.price_unit"
              :placeholder="t('WEBSITE.PRODUCTS.FORM.PRICE_UNIT_PLACEHOLDER')"
            />
          </div>
        </div>

        <!-- Stock et Poids -->
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label
              class="block text-sm font-medium text-n-slate-12 mb-1.5"
              for="product-stock"
            >
              {{ t('WEBSITE.PRODUCTS.FORM.STOCK') }}
            </label>
            <Input
              id="product-stock"
              v-model.number="formData.stock"
              type="number"
              min="0"
              :placeholder="t('WEBSITE.PRODUCTS.FORM.STOCK_PLACEHOLDER')"
            />
          </div>

          <div>
            <label
              class="block text-sm font-medium text-n-slate-12 mb-1.5"
              for="product-weight"
            >
              {{ t('WEBSITE.PRODUCTS.FORM.WEIGHT') }}
            </label>
            <Input
              id="product-weight"
              v-model.number="formData.weight"
              type="number"
              step="0.001"
              min="0"
              :placeholder="t('WEBSITE.PRODUCTS.FORM.WEIGHT_PLACEHOLDER')"
            />
          </div>
        </div>

        <!-- Actif -->
        <div class="flex items-center gap-3">
          <Checkbox id="product-active" v-model="formData.active" />
          <label
            class="text-sm font-medium text-n-slate-12 cursor-pointer"
            for="product-active"
          >
            {{ t('WEBSITE.PRODUCTS.FORM.ACTIVE') }}
          </label>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div
      class="flex items-center justify-end gap-3 px-6 py-4 border-t border-n-weak"
    >
      <Button
        :label="t('WEBSITE.PRODUCTS.FORM.CANCEL')"
        variant="outline"
        color="slate"
        @click="handleClose"
      />
      <Button
        :label="
          isEdit
            ? t('WEBSITE.PRODUCTS.FORM.SAVE')
            : t('WEBSITE.PRODUCTS.FORM.CREATE')
        "
        :loading="loading"
        @click="handleSubmit"
      />
    </div>

    <!-- Category Form Drawer (nested) -->
    <ProductCategoryForm
      v-if="showCategoryForm"
      @close="closeCategoryForm"
      @save="handleCategorySave"
    />
  </Drawer>
</template>
